/**
 * PixiCamera.js - WebGL Camera System using PixiJS Containers
 *
 * Manages the camera viewport using PixiJS container transforms.
 * The world container is translated to keep the player centered.
 */

const PixiCamera = {
  // Container that holds all world-space objects
  worldContainer: null,

  // Screen dimensions
  screenWidth: 0,
  screenHeight: 0,

  // Camera position (follows player)
  x: 0,
  y: 0,

  // Target position (for smooth camera)
  targetX: 0,
  targetY: 0,

  // Smoothing coefficient for camera easing (0 = frozen, 1 = instant)
  // Local player already applies render smoothing, so default to instant camera response
  smoothing: 1,

  /**
   * Initialize the camera system
   * @param {PIXI.Container} worldContainer - Container for world objects
   * @param {number} screenWidth - Screen width
   * @param {number} screenHeight - Screen height
   */
  init(worldContainer, screenWidth, screenHeight) {
    this.worldContainer = worldContainer;
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
  },

  /**
   * Update screen dimensions (on resize)
   * @param {number} width - New width
   * @param {number} height - New height
   */
  resize(width, height) {
    this.screenWidth = width;
    this.screenHeight = height;
  },

  /**
   * Update camera to follow a target position with smooth interpolation
   * @param {number} targetX - Target X position (player)
   * @param {number} targetY - Target Y position (player)
   */
  follow(targetX, targetY) {
    this.targetX = targetX;
    this.targetY = targetY;

    // Smooth camera movement if smoothing < 1, otherwise snap to target (default)
    if (this.smoothing >= 1) {
      this.x = this.targetX;
      this.y = this.targetY;
    } else if (this.smoothing > 0) {
      this.x += (this.targetX - this.x) * this.smoothing;
      this.y += (this.targetY - this.y) * this.smoothing;
    }

    // Update world container position to center on camera
    if (this.worldContainer) {
      // Use non-rounded positions for smooth sub-pixel rendering
      this.worldContainer.x = this.screenWidth / 2 - this.x;
      this.worldContainer.y = this.screenHeight / 2 - this.y;
    }
  },

  /**
   * Convert world coordinates to screen coordinates
   * @param {number} worldX - World X position
   * @param {number} worldY - World Y position
   * @returns {{x: number, y: number}} - Screen position
   */
  worldToScreen(worldX, worldY) {
    return {
      x: worldX - this.x + this.screenWidth / 2,
      y: worldY - this.y + this.screenHeight / 2
    };
  },

  /**
   * Convert screen coordinates to world coordinates
   * @param {number} screenX - Screen X position
   * @param {number} screenY - Screen Y position
   * @returns {{x: number, y: number}} - World position
   */
  screenToWorld(screenX, screenY) {
    return {
      x: screenX + this.x - this.screenWidth / 2,
      y: screenY + this.y - this.screenHeight / 2
    };
  },

  /**
   * Get visible world bounds
   * @returns {{left: number, right: number, top: number, bottom: number}}
   */
  getVisibleBounds() {
    const halfWidth = this.screenWidth / 2;
    const halfHeight = this.screenHeight / 2;
    return {
      left: this.x - halfWidth,
      right: this.x + halfWidth,
      top: this.y - halfHeight,
      bottom: this.y + halfHeight
    };
  }
};

// Export for use in other modules

export { PixiCamera };
